import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, X, Filter, Search,
  Plane, GraduationCap, TrendingUp, UserMinus, UserCheck, Bell, Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";

const TYPE_META = {
  pto: { label: "PTO", icon: Plane, color: "bg-blue-100 text-blue-700 border-blue-200" },
  training_due: { label: "Training", icon: GraduationCap, color: "bg-amber-100 text-amber-700 border-amber-200" },
  evaluation_due: { label: "Evaluation", icon: TrendingUp, color: "bg-violet-100 text-violet-700 border-violet-200" },
  follow_up_90: { label: "90-day follow-up", icon: UserCheck, color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  termination: { label: "Termination", icon: UserMinus, color: "bg-rose-100 text-rose-700 border-rose-200" },
  reminder: { label: "Reminder", icon: Bell, color: "bg-slate-200 text-slate-700 border-slate-300" },
};

const ALL_TYPES = Object.keys(TYPE_META);

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function fmt(d) { return d.toISOString().slice(0, 10); }

export default function Planner() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isPriv = ["hr", "admin", "ceo"].includes(user?.role);
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState("calendar"); // calendar | list
  const [scope, setScope] = useState("mine");
  const [activeTypes, setActiveTypes] = useState(new Set(ALL_TYPES));
  const [events, setEvents] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterDept, setFilterDept] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [filterName, setFilterName] = useState("");
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);

  useEffect(() => {
    if (!isPriv) return;
    api.get("/departments").then((r) => setDepartments(r.data));
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active !== false && u.role !== "admin")));
  }, [isPriv]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        date_from: fmt(monthStart),
        date_to: fmt(monthEnd),
        scope,
        types: Array.from(activeTypes).join(","),
      });
      if (isPriv) {
        if (filterDept) params.append("department_ids", filterDept);
        if (filterUserId) params.append("user_ids", filterUserId);
        if (filterName) params.append("q", filterName);
      }
      const r = await api.get(`/planner/events?${params.toString()}`);
      setEvents(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor, scope, activeTypes, filterDept, filterUserId, filterName]);

  // Calendar grid
  const weeks = useMemo(() => {
    const first = monthStart;
    const startDow = first.getDay();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let day = 1; day <= monthEnd.getDate(); day++) {
      cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), day));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [cursor, monthStart, monthEnd]);

  const eventsByDay = useMemo(() => {
    const m = {};
    for (const e of events) {
      const k = e.date;
      if (!m[k]) m[k] = [];
      m[k].push(e);
    }
    return m;
  }, [events]);

  const toggleType = (t) => {
    const s = new Set(activeTypes);
    if (s.has(t)) s.delete(t); else s.add(t);
    setActiveTypes(s);
  };

  const monthLabel = cursor.toLocaleString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><CalendarDays size={22} /></div>
          <div>
            <h1 className="sw-heading text-[28px]">Planner</h1>
            <p className="text-[14px] text-[#747474]">{t("plan.subtitle")}</p>
          </div>
        </div>
        <button data-testid="new-reminder-btn" onClick={() => setShowNew(true)} className="sw-btn-primary"><Plus size={14} />  {t("plan.new_reminder")}</button>
      </div>

      {/* Filters */}
      <div className="sw-card !p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#475569]">Scope:</span>
          {[
            { k: "mine", label: "Mine" },
            { k: "team", label: "My team" },
            ...(isPriv ? [{ k: "company", label: "Company" }] : []),
          ].map((s) => (
            <button
              key={s.k}
              data-testid={`scope-${s.k}`}
              onClick={() => setScope(s.k)}
              className={`px-3 py-1 rounded-full text-[12px] ${scope === s.k ? "bg-[#475569] text-white" : "bg-[#f1f5f9] text-[#475569]"}`}
            >{s.label}</button>
          ))}
          <div className="border-l border-[#e2e8f0] mx-2 h-5" />
          <span className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#475569]">{t("plan.view")}</span>
          <button onClick={() => setView("calendar")} data-testid="view-calendar" className={`px-3 py-1 rounded-full text-[12px] ${view === "calendar" ? "bg-[#475569] text-white" : "bg-[#f1f5f9] text-[#475569]"}`}>Calendar</button>
          <button onClick={() => setView("list")} data-testid="view-list" className={`px-3 py-1 rounded-full text-[12px] ${view === "list" ? "bg-[#475569] text-white" : "bg-[#f1f5f9] text-[#475569]"}`}>{t("plan.list")}</button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={13} className="text-[#94a3b8]" />
          {ALL_TYPES.map((t) => {
            const meta = TYPE_META[t];
            const Icon = meta.icon;
            const on = activeTypes.has(t);
            return (
              <button
                key={t}
                data-testid={`type-toggle-${t}`}
                onClick={() => toggleType(t)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${on ? meta.color : "bg-white border-[#e2e8f0] text-[#94a3b8] line-through"}`}
              >
                <Icon size={11} /> {meta.label}
              </button>
            );
          })}
        </div>

        {isPriv && (
          <div className="flex flex-wrap gap-2 items-center pt-2 border-t border-[#f1f5f9]">
            <span className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#475569] mr-1">HR filters:</span>
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
              <input
                data-testid="planner-search-name"
                placeholder={t("plan.search_ph")}
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                className="sw-input !pl-7 !py-1 !text-[12px] !h-[28px] w-[180px]"
              />
            </div>
            <select
              data-testid="planner-dept-filter"
              value={filterDept}
              onChange={(e) => setFilterDept(e.target.value)}
              className="sw-input !py-1 !text-[12px] !h-[28px]"
            >
              <option value="">{t("admin.all_depts")}</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <select
              data-testid="planner-user-filter"
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              className="sw-input !py-1 !text-[12px] !h-[28px]"
            >
              <option value="">{t("plan.all_employees")}</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            {(filterDept || filterUserId || filterName) && (
              <button onClick={() => { setFilterDept(""); setFilterUserId(""); setFilterName(""); }} className="text-[11px] text-[#475569] underline">{t("common.clear")}</button>
            )}
          </div>
        )}
      </div>

      {/* Month nav */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="w-9 h-9 rounded-full bg-white border border-[#e2e8f0] flex items-center justify-center hover:bg-[#f1f5f9]"><ChevronLeft size={16} /></button>
          <button onClick={() => setCursor(new Date())} className="text-[12px] px-3 py-1.5 rounded-full bg-white border border-[#e2e8f0] hover:bg-[#f1f5f9]">Today</button>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="w-9 h-9 rounded-full bg-white border border-[#e2e8f0] flex items-center justify-center hover:bg-[#f1f5f9]"><ChevronRight size={16} /></button>
        </div>
        <div className="text-[16px] font-semibold text-[#1e293b]">{monthLabel}</div>
        <div className="text-[12px] text-[#94a3b8]">{loading ? "Loading…" : `${events.length} event(s)`}</div>
      </div>

      {/* View */}
      {view === "calendar" ? (
        <div className="sw-card !p-0 overflow-hidden">
          <div className="grid grid-cols-7 border-b border-[#e2e8f0] bg-[#f8fafc]">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] font-semibold text-[#94a3b8] text-center border-r border-[#e2e8f0] last:border-r-0">{d}</div>
            ))}
          </div>
          {weeks.map((row, wi) => (
            <div key={wi} className="grid grid-cols-7">
              {row.map((day, di) => {
                const key = day ? fmt(day) : `${wi}-${di}`;
                const dayEvents = day ? eventsByDay[fmt(day)] || [] : [];
                const isToday = day && fmt(day) === fmt(new Date());
                return (
                  <div key={key} className={`min-h-[100px] p-2 border-r border-b border-[#e2e8f0] last:border-r-0 ${day ? "" : "bg-[#fafafa]"}`}>
                    {day && (
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[12px] ${isToday ? "bg-[#475569] text-white rounded-full w-6 h-6 flex items-center justify-center font-semibold" : "text-[#475569]"}`}>{day.getDate()}</span>
                      </div>
                    )}
                    <div className="flex flex-col gap-1">
                      {dayEvents.slice(0, 3).map((e) => {
                        const meta = TYPE_META[e.type];
                        const Icon = meta.icon;
                        return (
                          <button
                            key={e.id}
                            data-testid={`event-${e.id}`}
                            onClick={() => setSelected(e)}
                            className={`text-left text-[11px] px-1.5 py-0.5 rounded-[6px] border ${meta.color} truncate hover:shadow-sm`}
                            title={e.title}
                          >
                            <Icon size={10} className="inline mr-1" />
                            {e.title.length > 22 ? e.title.slice(0, 22) + "…" : e.title}
                          </button>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <button onClick={() => setSelected({ type: "day", date: fmt(day), items: dayEvents })} className="text-[10px] text-[#475569] hover:underline text-left">+{dayEvents.length - 3} more</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {events.length === 0 ? (
            <div className="sw-card text-center text-[13px] text-[#94a3b8]">{t("plan.no_events")}</div>
          ) : (
            events.map((e) => {
              const meta = TYPE_META[e.type];
              const Icon = meta.icon;
              return (
                <button
                  key={e.id}
                  data-testid={`event-list-${e.id}`}
                  onClick={() => setSelected(e)}
                  className="sw-card !p-3 flex items-center gap-3 text-left hover:shadow-md transition-all"
                >
                  <div className={`w-9 h-9 rounded-[10px] flex items-center justify-center ${meta.color}`}><Icon size={15} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-medium text-[#1e293b] truncate">{e.title}</div>
                    <div className="text-[11px] text-[#94a3b8]">{meta.label} · {e.date}{e.end_date && e.end_date !== e.date ? ` → ${e.end_date}` : ""}</div>
                  </div>
                  <span className="text-[10px] text-[#475569]">→</span>
                </button>
              );
            })
          )}
        </div>
      )}

      {selected && <EventDetailModal event={selected} onClose={() => setSelected(null)} onReload={load} />}
      {showNew && <NewReminderModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} isPriv={isPriv} users={users} />}
    </div>
  );
}

function EventDetailModal({ event, onClose, onReload }) {
  const { t } = useI18n();
  // Day summary (multiple events)
  if (event.type === "day") {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-[20px] max-w-[480px] w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between mb-4">
            <h3 className="sw-heading text-[18px]">Events on {event.date}</h3>
            <button onClick={onClose} className="text-[#94a3b8]"><X size={18} /></button>
          </div>
          <div className="flex flex-col gap-2">
            {event.items.map((e) => {
              const meta = TYPE_META[e.type];
              const Icon = meta.icon;
              return (
                <div key={e.id} className="flex items-center gap-2 text-[13px] py-2 border-b border-[#f1f5f9] last:border-0">
                  <div className={`w-7 h-7 rounded-[8px] flex items-center justify-center ${meta.color}`}><Icon size={12} /></div>
                  <span className="flex-1 text-[#1e293b]">{e.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
  const meta = TYPE_META[event.type] || TYPE_META.reminder;
  const Icon = meta.icon;
  const isReminder = event.type === "reminder";

  const remove = async () => {
    if (!confirm(t("plan.delete_reminder"))) return;
    try {
      await api.delete(`/planner/reminders/${event.source_id}`);
      onClose();
      onReload && onReload();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="event-detail-modal" className="bg-white rounded-[20px] max-w-[480px] w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-[10px] flex items-center justify-center ${meta.color}`}><Icon size={16} /></div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{meta.label}</div>
              <h3 className="sw-heading text-[18px] mt-0.5">{event.title}</h3>
            </div>
          </div>
          <button onClick={onClose} className="text-[#94a3b8]"><X size={18} /></button>
        </div>
        <div className="text-[13px] text-[#475569] py-3 border-t border-[#f1f5f9] flex flex-col gap-1">
          <div><span className="text-[#94a3b8] text-[11px] uppercase tracking-wider mr-1">When:</span>{event.date}{event.end_date && event.end_date !== event.date ? ` → ${event.end_date}` : ""}</div>
          {event.user_name && <div><span className="text-[#94a3b8] text-[11px] uppercase tracking-wider mr-1">Person:</span>{event.user_name}</div>}
          {event.meta?.description && <div className="italic mt-1">{event.meta.description}</div>}
          {event.meta?.cycle_name && <div><span className="text-[#94a3b8] text-[11px] uppercase tracking-wider mr-1">{t("plan.cycle")}</span>{event.meta.cycle_name}</div>}
          {event.meta?.hire_date && <div><span className="text-[#94a3b8] text-[11px] uppercase tracking-wider mr-1">Hired:</span>{event.meta.hire_date.slice(0,10)}</div>}
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-[#f1f5f9]">
          <Link to={event.link} onClick={onClose} className="text-[13px] text-[#475569] hover:underline">Open in source page →</Link>
          {isReminder && (
            <button data-testid="delete-reminder-btn" onClick={remove} className="text-[12px] text-[#881a27] hover:bg-rose-50 px-2 py-1 rounded inline-flex items-center gap-1"><Trash2 size={12} />  {t("common.delete")}</button>
          )}
        </div>
      </div>
    </div>
  );
}

function NewReminderModal({ onClose, onSaved, isPriv, users }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ title: "", date: "", description: "", user_id: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { title: form.title, date: form.date, description: form.description };
      if (isPriv && form.user_id) body.user_id = form.user_id;
      await api.post("/planner/reminders", body);
      onSaved();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[20px] max-w-[440px] w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="sw-heading text-[18px]">{t("plan.new_reminder")}</h3>
          <button onClick={onClose} className="text-[#94a3b8]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <div>
            <label className="sw-label">Title *</label>
            <input data-testid="reminder-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">Date *</label>
            <input data-testid="reminder-date" required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">Description</label>
            <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sw-input" />
          </div>
          {isPriv && (
            <div>
              <label className="sw-label">{t("plan.for_employee")}</label>
              <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} className="sw-input">
                <option value="">Myself</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button data-testid="save-reminder-btn" type="submit" disabled={saving} className="sw-btn-primary">{saving ? "..." : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
